import { Pencil, UserCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataCard, DetailRow } from "@/components/ui/data-card";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";

interface UserCardProps {
  fullName: string | null;
  email: string;
  roleLabels: string[];
  isChecked: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onClick?: () => void;
  isDeletePending?: boolean;
}

/** Mobile/tablet representation of a tenant user row on the Admin page. */
export function UserCard({
  fullName,
  email,
  roleLabels,
  isChecked,
  onToggle,
  onEdit,
  onDelete,
  onClick,
  isDeletePending,
}: UserCardProps) {
  return (
    <DataCard onClick={onClick}>
      <DataCard.Header>
        <span onClick={(e) => e.stopPropagation()}>
          <Checkbox checked={isChecked} onCheckedChange={onToggle} aria-label={`Select ${email}`} />
        </span>
        <UserCircle className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium truncate">{fullName || "—"}</span>
        {roleLabels.length > 0 ? (
          <span className="px-1.5 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
            {roleLabels.length} {roleLabels.length === 1 ? "role" : "roles"}
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit} title="Edit user">
            <Pencil className="h-4 w-4" />
          </Button>
          <ConfirmDeleteButton
            onConfirm={onDelete}
            entityName="user"
            entityLabel={email}
            isPending={isDeletePending}
          />
        </span>
      </DataCard.Header>

      <div className="divide-y divide-border">
        <DetailRow
          label="Roles"
          value={
            roleLabels.length > 0 ? (
              <span className="flex flex-wrap justify-end gap-1">
                {roleLabels.map((label) => (
                  <Badge key={label} variant="secondary">
                    {label}
                  </Badge>
                ))}
              </span>
            ) : null
          }
        />
        <DetailRow label="Email" value={<span className="truncate">{email}</span>} />
      </div>
    </DataCard>
  );
}